import type { Database } from "../../__definitions__/database-definitions";
import type { ProgramNode, ProgramTree } from "../program-tree/program-tree";

export type Level = "none" | "read" | "edit";

export const levelLabels: Record<Level, string> = {
  none: "No access",
  read: "Read only",
  edit: "Create and edit",
};

type AccessView = Database["public"]["Views"]["contributor_program_access"]["Row"];

/** A contributor's access to one program, as the contributor_program_access view gives it. */
export interface Access {
  level: Level;
  /** The setting on the program, or null when the program follows the program above it. */
  setLevel: Level | null;
  /** The level the program gets from above, and the program it comes from. */
  inheritedLevel: Level;
  inheritedFrom: string | null;
}

const NO_ACCESS: Access = {
  level: "none",
  setLevel: null,
  inheritedLevel: "none",
  inheritedFrom: null,
};

const isLevel = (value: string | null): value is Level =>
  value === "none" || value === "read" || value === "edit";

/**
 * The access of each contributor to each program. The view leaves out a contributor and program with
 * no setting on or above the program, so their access is none.
 */
export class AccessIndex {
  private readonly rows = new Map<string, Access>();

  constructor(rows: AccessView[]) {
    for (const row of rows) {
      if (!row.person_id || !row.program_id || !isLevel(row.level)) continue;
      this.rows.set(`${row.person_id}:${row.program_id}`, {
        level: row.level,
        setLevel: isLevel(row.set_level) ? row.set_level : null,
        inheritedLevel: isLevel(row.inherited_level) ? row.inherited_level : "none",
        inheritedFrom: row.inherited_from,
      });
    }
  }

  get(personId: string, programId: string): Access {
    return this.rows.get(`${personId}:${programId}`) ?? NO_ACCESS;
  }
}

/** Each program below a program, visible or not, depth first and sorted by title, with its depth. */
export const descendantsOf = (
  tree: ProgramTree,
  programId: string,
): { node: ProgramNode; depth: number }[] => {
  const all = [...tree.byId.values()].toSorted((a, b) =>
    a.title.localeCompare(b.title, undefined, { sensitivity: "base" }),
  );
  const rows: { node: ProgramNode; depth: number }[] = [];
  const walk = (parentId: string, depth: number): void => {
    for (const node of all) {
      if (node.parent_id !== parentId) continue;
      rows.push({ node, depth });
      walk(node.id, depth + 1);
    }
  };

  walk(programId, 1);
  return rows;
};

export const plural = (count: number, word: string): string =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

export interface ContributorAccess {
  access: Access;
  /** The programs below with access, so a contributor with none here can see the name. */
  below: number;
  /** The programs below with their own setting. */
  exceptions: number;
}

/** A contributor's access to a program and the programs below it, for the Access dialog. */
export const contributorAccess = (
  index: AccessIndex,
  tree: ProgramTree,
  personId: string,
  programId: string,
): ContributorAccess => {
  const descendants = descendantsOf(tree, programId).map(({ node }) =>
    index.get(personId, node.id),
  );

  return {
    access: index.get(personId, programId),
    below: descendants.filter(({ level }) => level !== "none").length,
    exceptions: descendants.filter(({ setLevel }) => setLevel !== null).length,
  };
};

/** Whether the Access dialog lists a contributor on a program. */
export const isListed = ({ access, below }: ContributorAccess): boolean =>
  access.level !== "none" || below > 0 || access.setLevel !== null;

/** Where a contributor's level on a program comes from, as the Access dialog says it. */
export const accessSource = (
  { access, below }: ContributorAccess,
  tree: ProgramTree,
  descendantCount: number,
): { kind: "set" | "blocked" | "inherited" | "name-only"; text: string } => {
  const from = access.inheritedFrom ? tree.byId.get(access.inheritedFrom)?.title : undefined;

  if (access.setLevel && access.level !== "none")
    return {
      kind: "set",
      text: `Set here${descendantCount ? `, applies to ${plural(descendantCount, "subfolder")}` : ""}`,
    };
  if (access.setLevel)
    return {
      kind: "blocked",
      text: `Blocked here. Overrides ${levelLabels[access.inheritedLevel]} from ${from ?? "above"}`,
    };
  if (access.level !== "none")
    return { kind: "inherited", text: `Inherited from ${from ?? "above"}` };
  return { kind: "name-only", text: `Name only. Has access in ${plural(below, "subfolder")}` };
};

/** The title and text of the confirmation before a contributor's access on a program is removed. */
export const removeText = (
  { access, below, exceptions }: ContributorAccess,
  tree: ProgramTree,
  program: ProgramNode,
  name: string,
  descendantCount: number,
): { title: string; body: string; note: string | null } => {
  const parent = program.parent_id ? tree.byId.get(program.parent_id) : undefined;
  const from = access.inheritedFrom ? tree.byId.get(access.inheritedFrom)?.title : undefined;
  const removed = exceptions
    ? ` Their ${plural(exceptions, "subfolder exception")} will be removed too.`
    : "";
  const note =
    parent && access.inheritedLevel !== "none" && access.setLevel !== "none"
      ? `They'll still have ${levelLabels[access.inheritedLevel]} here, inherited from ${from ?? parent.title}. Change it there to remove it completely.`
      : null;

  if (access.setLevel && access.level !== "none")
    return {
      title: `Remove ${name} from ${program.title}?`,
      body: `They'll lose ${levelLabels[access.level]} access to ${program.title}${
        descendantCount ? ` and all ${plural(descendantCount, "subfolder")} inside it` : ""
      }.${removed}`,
      note,
    };
  if (access.setLevel && parent && access.inheritedLevel !== "none")
    return {
      title: `Remove the block on ${program.title}?`,
      body: `${name} will get access to ${program.title} again, following ${parent.title}.${removed}`,
      note,
    };
  return {
    title: `Remove ${name} from ${program.title}?`,
    body: `They'll lose access to the ${plural(below, "subfolder")} in ${program.title} where they currently have it, and will no longer see this program's name.`,
    note,
  };
};
